import {ReactNode} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Divider, Typography} from '@mui/material';

import {CultureTypeDistribution} from './CultureTypeDistribution';
import DateRangeDetails from './DateRangeDetails';
import {DepartmentDistributionBars} from './DepartmentDistributionBars';
import {GenderDistributionBars} from './GenderDistributionBars';
import {LanguageDistributionBars} from './LanguageDistributionBars';
import {LocationDistributionBars} from './LocationDistributionBars';
import {greenColors, orangeColors, neutral} from '../../../../../../../layouts/theme/designSystem';
import {Survey} from '../../../types/SurveyTypes';

interface Props {
  survey: Survey | undefined;
  state?: string | null;
  showCultureTypeDistribution?: boolean;
  asBadge?: boolean;
  hideMetadata?: boolean;
}

export default function StatisticNumbers({
  survey,
  state,
  showCultureTypeDistribution = false,
  asBadge = true,
  hideMetadata = false,
}: Props) {
  const {t} = useTranslation();

  const cardBackgroundColor = orangeColors[7];
  const primaryTextColor = greenColors[700];
  const secondaryTextColor = neutral[700];
  const tertiaryTextColor = neutral[500];
  const borderColor = orangeColors[3];

  const formatValue = (value?: number | string) => {
    if (value === undefined || value === null || value === '' || value === 'nil') return '---';
    return value;
  };

  const calculateManagerPercentage = (statistics: Survey['statistics'] | undefined) => {
    if (!statistics) {
      return;
    }

    const totalRespondents = statistics.totalRespondents ?? 0;

    const percentageManager =
      statistics.managerRespondents && totalRespondents > 0
        ? Math.round((statistics.managerRespondents / totalRespondents) * 100)
        : 0;

    return {manager: `${percentageManager}%`};
  };

  const renderOmittedMetricCard = (label: string, cardKey?: string) => (
    <Box
      key={cardKey}
      sx={{
        backgroundColor: cardBackgroundColor,
        borderRadius: '8px',
        padding: '0.75rem 1rem',
        minHeight: '82px',
      }}
    >
      <Typography
        sx={{
          color: tertiaryTextColor,
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.04em',
          lineHeight: 1.4,
          marginBottom: '4px',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: neutral[500],
          fontSize: '13px',
          lineHeight: 1.4,
        }}
      >
        {t('companySurveys.details.notCollectedInSurvey')}
      </Typography>
    </Box>
  );

  const renderMetricCard = (label: string, value: ReactNode, subText?: ReactNode, cardKey?: string) => (
    <Box
      key={cardKey}
      sx={{
        backgroundColor: cardBackgroundColor,
        borderRadius: '8px',
        padding: '0.75rem 1rem',
        minHeight: '82px',
      }}
    >
      <Typography
        sx={{
          color: tertiaryTextColor,
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.04em',
          lineHeight: 1.4,
          marginBottom: '4px',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: primaryTextColor,
          fontSize: value === '---' ? '18px' : '22px',
          fontWeight: 500,
          lineHeight: 1.25,
        }}
      >
        {value}
      </Typography>
      {subText ? (
        <Typography
          sx={{
            color: secondaryTextColor,
            fontSize: '12px',
            lineHeight: 1.4,
            marginTop: '2px',
          }}
        >
          {subText}
        </Typography>
      ) : null}
    </Box>
  );

  const managerPercentage = calculateManagerPercentage(survey?.statistics)?.manager ?? '0%';
  const averageAgeGroup = formatValue(survey?.statistics?.averageAgeGroup);
  const isAgeGroupOmitted = Boolean(survey?.configuration?.omitAgeGroup);
  const metricCards: ReactNode[] = [
    renderMetricCard(
      t('companySurveys.stats.respondents'),
      formatValue(survey?.statistics?.totalRespondents),
      undefined,
      'respondents'
    ),
    renderMetricCard(
      t('companySurveys.stats.managers'),
      formatValue(survey?.statistics?.managerRespondents),
      `${managerPercentage} ${t('companySurveys.details.managmentPercentage')}`,
      'managers'
    ),
    renderMetricCard(
      t('companySurveys.stats.employees'),
      formatValue(survey?.statistics?.employeeRespondents),
      undefined,
      'employees'
    ),
  ];

  if (isAgeGroupOmitted) {
    metricCards.push(renderOmittedMetricCard(t('companySurveys.details.averageAge'), 'age-group'));
  } else {
    metricCards.push(renderMetricCard(t('companySurveys.details.averageAge'), averageAgeGroup, undefined, 'age-group'));
  }

  const metricColumnCount = Math.max(metricCards.length, 1);

  return (
    <Box>
      {state && (
        <Box sx={{marginBottom: '1.25rem'}}>
          <DateRangeDetails survey={survey} state={state} asBadge={asBadge} hideMetadata={hideMetadata} />
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: '10px',
          gridTemplateColumns: {
            xs: '1fr',
            sm: `repeat(${Math.min(metricColumnCount, 2)}, minmax(0, 1fr))`,
            md: `repeat(${metricColumnCount}, minmax(0, 1fr))`,
          },
          marginBottom: '1.5rem',
        }}
      >
        {metricCards}
      </Box>

      <Divider sx={{borderColor, marginBottom: '1.25rem'}} />

      <Box
        sx={{
          display: 'grid',
          gap: '1rem',
          gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))'},
          marginBottom: '1.5rem',
        }}
      >
        <LanguageDistributionBars survey={survey} />
        <GenderDistributionBars survey={survey} />
      </Box>

      <Divider sx={{borderColor, marginBottom: '1.25rem'}} />

      <Box
        sx={{
          display: 'grid',
          gap: '1rem',
          gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))'},
          marginBottom: '1.5rem',
        }}
      >
        <DepartmentDistributionBars
          departments={survey?.statistics?.respondentsPerDepartment}
          isOmitted={survey?.configuration?.omitDepartment}
        />
        <LocationDistributionBars
          locations={survey?.statistics?.respondentsPerLocation}
          isOmitted={survey?.configuration?.omitLocation}
        />
      </Box>

      {showCultureTypeDistribution && (
        <>
          <Divider sx={{borderColor, marginBottom: '1.25rem'}} />
          <CultureTypeDistribution
            distribution={survey?.statistics?.cultureTypeDistribution ?? survey?.cultureTypeDistribution}
          />
        </>
      )}
    </Box>
  );
}
